import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { Text } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { cn } from "@/utils/cn";
import { openCookiePreferences, openDoNotSellModal } from "@/utils/osano";

interface FooterLinkProps {
  to?: string;
  className?: string;
  isExternal?: boolean;
  onClick?: React.EventHandler<React.MouseEvent>;
  children: React.ReactNode;
}

const FooterLink = ({
  children,
  className,
  isExternal = false,
  to,
  ...rest
}: FooterLinkProps) => {
  const linkClass = cn(
    "text-sm text-muted-foreground hover:text-gray-800 hover:underline dark:hover:text-gray-500",
    className,
  );

  if (isExternal) {
    return (
      <div>
        <a href={to} target="_blank" className={linkClass}>
          {children}
        </a>
      </div>
    );
  }

  if (!to) {
    return (
      <div>
        <button type="button" className={linkClass} {...rest}>
          {children}
        </button>
      </div>
    );
  }

  return (
    <div>
      <Link to={to} className={linkClass} {...rest}>
        {children}
      </Link>
    </div>
  );
};

const LinkFooter = () => {
  const { currentAccount, logOut } = useAuth();

  const onClickLogOut: React.EventHandler<React.MouseEvent> = (e) => {
    e.preventDefault();
    logOut();
  };

  const renderExternalLinks = (
    <>
      <FooterLink isExternal to="https://help.truthsocial.com">
        <FormattedMessage
          id="navigation_bar.help"
          defaultMessage="Help Center"
        />
      </FooterLink>
      <FooterLink isExternal to="https://help.truthsocial.com/legal">
        <FormattedMessage id="navigation_bar.legal" defaultMessage="Legal" />
      </FooterLink>
    </>
  );

  return (
    <div className="space-y-2">
      <div className="divide-x-dot flex flex-wrap items-center text-gray-600">
        {currentAccount ? (
          <>
            <FooterLink to="/blocks">
              <FormattedMessage
                id="navigation_bar.blocks"
                defaultMessage="Blocks"
              />
            </FooterLink>
            <FooterLink to="/mutes">
              <FormattedMessage
                id="navigation_bar.mutes"
                defaultMessage="Mutes"
              />
            </FooterLink>
            {renderExternalLinks}
            <FooterLink onClick={onClickLogOut}>
              <FormattedMessage
                id="navigation_bar.logout"
                defaultMessage="Logout"
              />
            </FooterLink>
            <FooterLink onClick={openCookiePreferences}>
              <FormattedMessage
                id="navigation_bar.cookiePreferences"
                defaultMessage="Cookie Preferences"
              />
            </FooterLink>
            <FooterLink onClick={openDoNotSellModal} className="text-start">
              <FormattedMessage
                id="navigation_bar.doNotSell"
                defaultMessage="Do Not Sell or Share My Personal Information"
              />
            </FooterLink>
          </>
        ) : (
          <>
            {renderExternalLinks}
            <FooterLink onClick={openCookiePreferences}>
              <FormattedMessage
                id="navigation_bar.cookiePreferences"
                defaultMessage="Cookie Preferences"
              />
            </FooterLink>
            <FooterLink onClick={openDoNotSellModal} className="text-start">
              <FormattedMessage
                id="navigation_bar.doNotSell"
                defaultMessage="Do Not Sell or Share My Personal Information"
              />
            </FooterLink>
          </>
        )}
      </div>

      <Text theme="muted" size="sm">
        <span className="inline-block align-middle">
          <FormattedMessage
            id="footer.made-in"
            defaultMessage="Proudly made in the United States of America. {flag}"
            values={{
              flag: (
                <span key="flag" aria-label="US flag" role="img">
                  🇺🇸
                </span>
              ),
            }}
          />
        </span>
      </Text>
    </div>
  );
};

export default LinkFooter;
